import { TeamActionTypes, TeamDriverRow, TeamMemberRow, TeamPeopleChange, TeamPeopleChangedMessage, TeamPersonKind } from '@numeo/types-main-web-app'
import type { AppDispatch, RootState } from 'store'
import { ListTeamDriversArg, ListTeamMembersArg, teamApi } from 'store/api/teamApi'
import { RowChange, patchTeamPage } from 'utils/team/teamCachePatch'
import { driverComparator, matchesDriverQuery, matchesMemberQuery } from 'utils/team/teamQuery'
import { driverWiderTier, memberWiderTier, personFromDriverRow, personFromMemberRow } from 'utils/team/teamRows'
import { log } from 'utils/logger'

/**
 * `team.peopleChanged`: someone else added, changed or removed members or drivers of this company. Patches the rows of
 * every cached directory page and the people cache in place (`updateQueryData` keeps each entry's request time, so the
 * five-minute lifetime is not restarted). A page that cannot place a change stays as it is until its lifetime ends. A
 * driver's whole record (`view=full`: their email and Telegram chat for a dispatch) cannot be patched from the base row
 * the change carries, so the records of the changed drivers are invalidated: re-read where on screen, dropped elsewhere.
 */

const changesOf = <T>(changes: readonly TeamPeopleChange[], kind: TeamPersonKind, rowOf: (change: TeamPeopleChange) => T | undefined): RowChange<T>[] =>
    changes.flatMap((change): RowChange<T>[] => {
        if (change.kind !== kind) return []
        if (change.op === 'remove') return [{ op: 'remove', id: change.id }]
        const row = rowOf(change)
        return row ? [{ op: 'upsert', id: change.id, row }] : []
    })

const memberRow = (change: TeamPeopleChange): TeamMemberRow | undefined => (change.kind === 'member' && change.op === 'upsert' ? change.row : undefined)
const driverRow = (change: TeamPeopleChange): TeamDriverRow | undefined => (change.kind === 'driver' && change.op === 'upsert' ? change.row : undefined)

/** Every id a cached list page of this company holds, and every person the people cache names. */
const knownIds = (state: RootState, applicationId: string): Set<string> => {
    const ids = new Set<string>()
    for (const args of teamApi.util.selectCachedArgsForQuery(state, 'listTeamMembers')) {
        if (args.applicationId !== applicationId) continue
        for (const row of teamApi.endpoints.listTeamMembers.select(args)(state).data?.items ?? []) ids.add(row.id)
    }
    for (const args of teamApi.util.selectCachedArgsForQuery(state, 'listTeamDrivers')) {
        if (args.applicationId !== applicationId) continue
        for (const row of teamApi.endpoints.listTeamDrivers.select(args)(state).data?.items ?? []) ids.add(row.id)
    }
    for (const args of teamApi.util.selectCachedArgsForQuery(state, 'getPerson')) {
        if (args.applicationId === applicationId && teamApi.endpoints.getPerson.select(args)(state).data) ids.add(args.id)
    }
    return ids
}

/** The people cache follows the rows: an upsert names (or renames) the person, a removal keeps the name and marks them removed. */
const patchPeople = (dispatch: AppDispatch, state: RootState, applicationId: string, changes: readonly TeamPeopleChange[]): void => {
    const named = changes.flatMap((change) => {
        if (change.op !== 'upsert') return []
        const person = change.kind === 'member' ? personFromMemberRow(change.row) : personFromDriverRow(change.row)
        return person ? [{ endpointName: 'getPerson' as const, arg: { applicationId, id: change.id }, value: person }] : []
    })
    if (named.length) dispatch(teamApi.util.upsertQueryEntries(named))
    const cached = new Set(
        teamApi.util
            .selectCachedArgsForQuery(state, 'getPerson')
            .filter((args) => args.applicationId === applicationId)
            .map((args) => args.id)
    )
    for (const change of changes) {
        if (change.op !== 'remove' || !cached.has(change.id)) continue
        dispatch(teamApi.util.updateQueryData('getPerson', { applicationId, id: change.id }, (person) => (person ? { ...person, removed: true } : person)))
    }
}

/** Apply one message's changes to every cached page and person of the company this tab shows. */
const applyTeamPeopleChanges = (dispatch: AppDispatch, getState: () => RootState, applicationId: string, changes: readonly TeamPeopleChange[]): void => {
    const state = getState()
    const known = knownIds(state, applicationId)
    const isKnown = (id: string): boolean => known.has(id)

    const members = changesOf(changes, 'member', memberRow)
    if (members.length) {
        for (const args of teamApi.util.selectCachedArgsForQuery(state, 'listTeamMembers')) {
            if (args.applicationId !== applicationId) continue
            const query: ListTeamMembersArg = args
            dispatch(
                teamApi.util.updateQueryData('listTeamMembers', args, (page) =>
                    patchTeamPage(page, members, { matches: (row) => matchesMemberQuery(row, query), isKnown, widerTier: memberWiderTier })
                )
            )
        }
    }
    const drivers = changesOf(changes, 'driver', driverRow)
    if (drivers.length) {
        for (const args of teamApi.util.selectCachedArgsForQuery(state, 'listTeamDrivers')) {
            if (args.applicationId !== applicationId) continue
            const query: ListTeamDriversArg = args
            const compare = driverComparator(query)
            dispatch(
                teamApi.util.updateQueryData('listTeamDrivers', args, (page) =>
                    patchTeamPage(page, drivers, { matches: (row) => matchesDriverQuery(row, query), isKnown, compare, widerTier: driverWiderTier })
                )
            )
        }
    }
    patchPeople(dispatch, state, applicationId, changes)
    const changedDrivers = [...new Set(changes.filter((change) => change.kind === 'driver').map((change) => change.id))]
    if (changedDrivers.length) dispatch(teamApi.util.invalidateTags(changedDrivers.map((id) => ({ type: 'TeamDriverFull' as const, id }))))
}

export const handleTeamMessages = (message: TeamPeopleChangedMessage, dispatch: AppDispatch, getState: () => RootState): boolean => {
    if (message.data?.action !== TeamActionTypes.PeopleChanged) return false
    const application = getState().auth.application
    // Same cross-company guard as `APP_ApplicationPatched`: a push for another company is cross-talk, never applied.
    if (!application?.id || application.projectName !== message.data.projectName) {
        log.w('Ignored a team change addressed to another company', {
            activeProjectName: application?.projectName,
            pushedProjectName: message.data.projectName,
        })
        return true
    }
    applyTeamPeopleChanges(dispatch, getState, String(application.id), message.data.changes ?? [])
    return true
}
